import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Button from '../../../ui/Button';
import { createGroundLootOperationId, groundLootErrorMessage } from './groundLoot';
import GroundLootDialogFrame from './GroundLootDialogFrame';

export default function GroundLootDropDialog({ request, onClose, onConfirm }) {
  const maximum = Math.max(1, Number(request?.item?.quantity) || 1);
  const [quantity, setQuantity] = useState(maximum);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const operationIdRef = useRef(createGroundLootOperationId());
  const preventFocusRestoreRef = useRef(false);
  const shouldRestoreFocus = useCallback(
    () => !preventFocusRestoreRef.current,
    [],
  );
  const getFallbackFocusTarget = useCallback(
    opener => opener?.closest('[role="grid"]') ?? null,
    [],
  );

  useEffect(() => {
    setQuantity(maximum);
    setBusy(false);
    setError('');
    operationIdRef.current = createGroundLootOperationId();
    preventFocusRestoreRef.current = false;
  }, [
    maximum,
    request?.item?.itemId,
    request?.item?.itemInstanceId,
    request?.target?.x,
    request?.target?.y,
  ]);

  const updateQuantity = value => {
    const next = Math.min(maximum, Math.max(1, Number(value) || 1));
    setQuantity(next);
    setError('');
    operationIdRef.current = createGroundLootOperationId();
  };

  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    preventFocusRestoreRef.current = true;
    try {
      await onConfirm(quantity, operationIdRef.current);
    } catch (submitError) {
      preventFocusRestoreRef.current = false;
      if (submitError?.groundLootUseNewOperationId) {
        operationIdRef.current = createGroundLootOperationId();
      }
      setError(groundLootErrorMessage(
        submitError,
        'The item could not be dropped. Your Pack has been refreshed; try again.',
      ));
      setBusy(false);
    }
  };

  const itemName = request?.itemDetails?.name || 'this item';

  return (
    <GroundLootDialogFrame
      busy={busy}
      className="ground-loot-dialog--confirm"
      description="The item will be visible to everyone in this region and disappear after five minutes."
      getFallbackFocusTarget={getFallbackFocusTarget}
      onClose={onClose}
      shouldRestoreFocus={shouldRestoreFocus}
      title="Drop item?"
    >
      <form className="ground-loot-drop-form" onSubmit={submit}>
        <dl className="ground-loot-drop-form__summary">
          <div>
            <dt>Item</dt>
            <dd>{itemName}</dd>
          </div>
          <div>
            <dt>Ground tile</dt>
            <dd>{request.target.x}, {request.target.y}</dd>
          </div>
        </dl>

        {maximum > 1 ? (
          <div className="ground-loot-drop-form__quantity">
            <label htmlFor="ground-loot-drop-quantity">Amount</label>
            <div>
              <input
                id="ground-loot-drop-quantity"
                inputMode="numeric"
                max={maximum}
                min="1"
                onChange={event => updateQuantity(event.target.value)}
                type="number"
                value={quantity}
              />
              <Button
                aria-pressed={quantity === maximum}
                onClick={() => updateQuantity(maximum)}
                variant="secondary"
              >
                All ({maximum})
              </Button>
            </div>
          </div>
        ) : (
          <p className="ground-loot-drop-form__single">Amount: 1</p>
        )}

        {error ? <p className="ground-loot-dialog__error" role="alert">{error}</p> : null}
        <div className="ground-loot-dialog__actions">
          <Button disabled={busy} onClick={onClose} variant="ghost">Keep item</Button>
          <Button disabled={busy} type="submit" variant="danger">
            {busy ? 'Dropping…' : `Drop ${quantity}`}
          </Button>
        </div>
      </form>
    </GroundLootDialogFrame>
  );
}
